import { t } from "../i18n"
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["progressBar", "progressText", "signButton", "item", "saveButton"]
  static values = { 
    signatureId: Number,
    items: Array
  }

  connect() {
    console.log("Checklist completion controller connected")
    this.localItems = this.itemsValue || []
    this.hasChanges = false
    this.updateProgress()
    this.restoreUIState()
  }

  restoreUIState() {
    // Restore UI state for all items based on saved data
    this.localItems.forEach((item, index) => {
      const itemEl = this.element.querySelector(`[data-checklist-item-id="${item.id}"]`)
      if (!itemEl) return

      // Restore pass/fail button states
      if (item.passed !== undefined && item.passed !== null) {
        const buttons = itemEl.querySelectorAll('button[data-action*="submitPassFail"]')
        buttons.forEach(btn => {
          const btnValue = btn.dataset.value === "true"
          const isSelected = (item.passed === true && btnValue) || (item.passed === false && !btnValue)
          
          // Reset classes
          btn.className = "flex-1 px-4 py-3 rounded-lg border-2 transition-all"
          
          if (isSelected) {
            if (btnValue) {
              btn.classList.add('bg-green-600', 'border-green-500', 'text-white')
            } else {
              btn.classList.add('bg-red-600', 'border-red-500', 'text-white')
            }
          } else {
            btn.classList.add('bg-gray-800', 'border-gray-600', 'text-gray-300')
            if (btnValue) {
              btn.classList.add('hover:border-green-500')
            } else {
              btn.classList.add('hover:border-red-500')
            }
          }
        })
      }

      // Restore number input values
      if (item.number_value !== undefined && item.number_value !== null) {
        const numberInput = itemEl.querySelector(`input[type="number"][data-item-id="${item.id}"]`)
        if (numberInput) {
          numberInput.value = item.number_value
        }
      }

      // Restore text input values
      if (item.text_value !== undefined && item.text_value !== null) {
        const textInput = itemEl.querySelector(`textarea[data-item-id="${item.id}"]`)
        if (textInput) {
          textInput.value = item.text_value
        }
      }

      // Restore checkbox values
      if (item.boolean_value !== undefined && item.boolean_value !== null) {
        const checkbox = itemEl.querySelector(`input[type="checkbox"][data-item-id="${item.id}"]`)
        if (checkbox) {
          checkbox.checked = item.boolean_value
        }
      }

      // Restore dropdown values
      const dropdownValue = item.dropdown_value || item.text_value
      if (dropdownValue) {
        const selectEl = itemEl.querySelector(`select[data-item-id="${item.id}"]`)
        if (selectEl) {
          selectEl.value = dropdownValue
        }
      }

      // Restore completion indicator
      const isCompleted = item.completed ||
             (item.passed !== null && item.passed !== undefined) ||
             (item.text_value && item.text_value.length > 0) ||
             (item.dropdown_value && item.dropdown_value.length > 0) ||
             (item.number_value !== null && item.number_value !== undefined) ||
             (item.boolean_value !== null && item.boolean_value !== undefined)
      const indicator = itemEl.querySelector('.material-symbols-outlined:last-child')
      if (indicator) {
        if (isCompleted) {
          indicator.textContent = 'check_circle' // Material Symbols ligature
          indicator.classList.remove('text-gray-600')
          indicator.classList.add('text-green-400')
        } else {
          indicator.textContent = 'radio_button_unchecked' // Material Symbols ligature
          indicator.classList.remove('text-green-400')
          indicator.classList.add('text-gray-600')
        }
      }
    })
  }

  submitPassFail(event) {
    const button = event.currentTarget
    const itemId = parseInt(button.dataset.itemId)
    const value = button.dataset.value === "true"
    
    this.updateLocalItem(itemId, { passed: value })
    
    // Update UI for pass/fail buttons
    const container = button.closest('.flex')
    const buttons = container.querySelectorAll('button')
    buttons.forEach(btn => {
      const btnValue = btn.dataset.value === "true"
      const isSelected = btnValue === value
      
      // Reset classes
      btn.className = "flex-1 px-4 py-3 rounded-lg border-2 transition-all"
      
      if (isSelected) {
        if (value) {
          btn.classList.add('bg-green-600', 'border-green-500', 'text-white')
        } else {
          btn.classList.add('bg-red-600', 'border-red-500', 'text-white')
        }
      } else {
        btn.classList.add('bg-gray-800', 'border-gray-600', 'text-gray-300')
        if (btnValue) {
          btn.classList.add('hover:border-green-500')
        } else {
          btn.classList.add('hover:border-red-500')
        }
      }
    })
  }

  submitCheckbox(event) {
    const checkbox = event.target
    const itemId = parseInt(checkbox.dataset.itemId)
    const value = checkbox.checked
    
    this.updateLocalItem(itemId, { completed: value, boolean_value: value })
  }

  submitText(event) {
    const textarea = event.target
    const itemId = parseInt(textarea.dataset.itemId)
    const value = textarea.value
    
    this.updateLocalItem(itemId, { text_value: value })
  }

  submitNumber(event) {
    const input = event.target
    const itemId = parseInt(input.dataset.itemId)
    const value = parseFloat(input.value)
    
    if (!isNaN(value)) {
      this.updateLocalItem(itemId, { number_value: value })
    }
  }

  submitDropdown(event) {
    const select = event.target
    const itemId = parseInt(select.dataset.itemId)
    const value = select.value

    if (value !== "") {
      this.updateLocalItem(itemId, { dropdown_value: value })
    }
  }

  updateLocalItem(itemId, changes) {
    const itemIndex = this.localItems.findIndex(item => item.id === itemId)
    if (itemIndex !== -1) {
      this.localItems[itemIndex] = { ...this.localItems[itemIndex], ...changes }
      
      // Update completion status — treat explicit false for boolean/pass_fail as a completed answer
      const item = this.localItems[itemIndex]
      const isCompleted = item.completed ||
             (item.passed !== null && item.passed !== undefined) ||
             (item.text_value && item.text_value.length > 0) ||
             (item.dropdown_value && item.dropdown_value.length > 0) ||
             (item.number_value !== null && item.number_value !== undefined) ||
             (item.boolean_value !== null && item.boolean_value !== undefined)
      this.localItems[itemIndex].completed = !!isCompleted
      
      this.hasChanges = true
      this.updateProgress()
      this.showSaveButton()
      
      // Update item completion indicator
      const itemEl = this.element.querySelector(`[data-checklist-item-id="${itemId}"]`)
      if (itemEl) {
        const indicator = itemEl.querySelector('.material-symbols-outlined:last-child')
        if (indicator) {
          if (isCompleted) {
            indicator.textContent = 'check_circle' // Material Symbols ligature
            indicator.classList.remove('text-gray-600')
            indicator.classList.add('text-green-400')
          } else {
            indicator.textContent = 'radio_button_unchecked' // Material Symbols ligature
            indicator.classList.remove('text-green-400')
            indicator.classList.add('text-gray-600')
          }
        }
      }
    }
  }

  showSaveButton() {
    if (this.hasSaveButtonTarget) {
      this.saveButtonTarget.classList.remove('hidden')
      this.saveButtonTarget.classList.add('animate-pulse')
    }
  }

  async save() {
    if (!this.hasChanges) return

    const button = this.hasSaveButtonTarget ? this.saveButtonTarget : null
    if (button) {
      button.disabled = true
      button.classList.remove('animate-pulse')
      button.innerHTML = `
        <span class="material-symbols-outlined animate-spin !text-lg">progress_activity</span>
        ${t("javascript.controllers.checklist_completion_controller.saving")}
      `
    }

    try {
      const response = await fetch(`/api/document_signatures/${this.signatureIdValue}/checklist_responses/bulk_update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken,
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          checklist_items: this.localItems
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()

      if (data.success) {
        this.hasChanges = false
        if (this.hasSaveButtonTarget) {
          this.saveButtonTarget.classList.add('hidden')
          this.saveButtonTarget.classList.remove('animate-pulse')
          this.saveButtonTarget.disabled = false
          this.saveButtonTarget.innerHTML = `
            <span class="material-symbols-outlined !text-lg">save</span>
            ${t("javascript.controllers.checklist_completion_controller.save_checklist_progress")}
          `
        }
        
        // Show success message
        this.showSuccessMessage(t('javascript.controllers.checklist_completion_controller.checklist_saved'))
      } else {
        throw new Error(data.message || t('javascript.controllers.checklist_completion_controller.failed_to_save_checklist'))
      }
    } catch (error) {
      console.error('Error saving checklist:', error)
      alert(t('javascript.controllers.checklist_completion_controller.failed_to_save_checklist_with_error', { error: error.message }))
      if (button) {
        button.disabled = false
        button.innerHTML = `
          <span class="material-symbols-outlined !text-lg">save</span>
          ${t("javascript.controllers.checklist_completion_controller.save_checklist_progress")}
        `
        button.classList.add('animate-pulse')
      }
    }
  }

  showSuccessMessage(message) {
    // Create a temporary success message
    const messageEl = document.createElement('div')
    messageEl.className = 'fixed top-4 right-4 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg z-50'
    messageEl.textContent = message
    document.body.appendChild(messageEl)
    
    setTimeout(() => {
      messageEl.remove()
    }, 3000)
  }

  async submitResponse(itemId, value) {
    try {
      const response = await fetch(`/api/document_signatures/${this.signatureIdValue}/checklist_responses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken,
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          checklist_item_id: itemId,
          value: value
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()

      if (data.success) {
        this.updateProgress(data.data.progress)
        this.markItemComplete(itemId)
      } else {
        console.error('Failed to submit response:', data)
        alert(t('javascript.controllers.checklist_completion_controller.failed_to_submit_response_please_try_again'))
      }
    } catch (error) {
      console.error('Error submitting response:', error)
      alert(t('javascript.controllers.checklist_completion_controller.an_error_occurred_please_try_again'))
    }
  }

  updateProgress() {
    // Calculate progress from local items
    const totalItems = this.localItems.length
    if (totalItems === 0) return

    const completedItems = this.localItems.filter(item => {
      return item.completed ||
             (item.passed !== null && item.passed !== undefined) ||
             (item.text_value && item.text_value.length > 0) ||
             (item.dropdown_value && item.dropdown_value.length > 0) ||
             (item.number_value !== null && item.number_value !== undefined) ||
             (item.boolean_value !== null && item.boolean_value !== undefined)
    }).length

    const percentage = (completedItems / totalItems) * 100

    // Update progress bar
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${percentage}%`
    }

    // Update progress text
    if (this.hasProgressTextTarget) {
      this.progressTextTarget.textContent = `${Math.round(percentage)}%`
    }

    // Check if all required items are completed
    const requiredItems = this.localItems.filter(item => item.required)
    const completedRequiredItems = requiredItems.filter(item => {
      return item.completed ||
             (item.passed !== null && item.passed !== undefined) ||
             (item.text_value && item.text_value.length > 0) ||
             (item.dropdown_value && item.dropdown_value.length > 0) ||
             (item.number_value !== null && item.number_value !== undefined) ||
             (item.boolean_value !== null && item.boolean_value !== undefined)
    })
    const canSign = requiredItems.length === completedRequiredItems.length

    // Enable/disable sign button
    if (this.hasSignButtonTarget) {
      this.signButtonTarget.disabled = !canSign

      // Update button text
      const buttonSpan = this.signButtonTarget.querySelector('span:last-child')
      if (buttonSpan) {
        if (canSign) {
          buttonSpan.textContent = t('javascript.controllers.checklist_completion_controller.sign_document')
        } else {
          buttonSpan.textContent = t('javascript.controllers.checklist_completion_controller.complete_checklist_first')
        }
      }

      // Add pulse animation when can sign
      if (canSign) {
        this.signButtonTarget.classList.add('animate-pulse')
        setTimeout(() => {
          this.signButtonTarget.classList.remove('animate-pulse')
        }, 2000)
      }
    }
  }

  markItemComplete(itemId) {
    const item = this.itemTargets.find(el => el.dataset.checklistItemId === itemId.toString())
    if (!item) return

    // Find the completion indicator icon
    const indicator = item.querySelector('.material-symbols-outlined:last-child')
    if (indicator) {
      indicator.textContent = 'check_circle' // Material Symbols ligature
      indicator.classList.remove('text-gray-600')
      indicator.classList.add('text-green-400')
    }
  }

  showFailureModal(itemId) {
    // Create failure modal dynamically
    const modal = document.createElement('div')
    modal.className = 'fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4'
    modal.innerHTML = `
      <div class="bg-[#1C2A38] border border-red-700 rounded-lg max-w-lg w-full p-6">
        <h3 class="text-white text-xl font-semibold mb-4 flex items-center gap-2">
          <span class="material-symbols-outlined text-red-400">warning</span>
          ${t("javascript.controllers.checklist_completion_controller.report_failure")}
        </h3>

        <form id="failure-form">
          <div class="space-y-4">
            <div>
              <label class="block text-gray-300 text-sm font-medium mb-2">${t("javascript.controllers.checklist_completion_controller.severity_required")}</label>
              <select name="severity" required class="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-red-500 focus:ring-1 focus:ring-red-500">
                <option value="low">${t("javascript.controllers.checklist_completion_controller.severity_low")}</option>
                <option value="medium" selected>${t("javascript.controllers.checklist_completion_controller.severity_medium")}</option>
                <option value="high">${t("javascript.controllers.checklist_completion_controller.severity_high")}</option>
                <option value="critical">${t("javascript.controllers.checklist_completion_controller.severity_critical")}</option>
              </select>
            </div>

            <div>
              <label class="block text-gray-300 text-sm font-medium mb-2">${t("javascript.controllers.checklist_completion_controller.description_required")}</label>
              <textarea name="description" required rows="4" placeholder="${this.#escAttr(t("javascript.controllers.checklist_completion_controller.description_placeholder"))}" class="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-red-500 focus:ring-1 focus:ring-red-500"></textarea>
            </div>

            <div>
              <label class="block text-gray-300 text-sm font-medium mb-2">${t("javascript.controllers.checklist_completion_controller.corrective_action")}</label>
              <textarea name="corrective_action" rows="3" placeholder="${this.#escAttr(t("javascript.controllers.checklist_completion_controller.corrective_action_placeholder"))}" class="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-red-500 focus:ring-1 focus:ring-red-500"></textarea>
            </div>

            <div>
              <label class="block text-gray-300 text-sm font-medium mb-2">${t("javascript.controllers.checklist_completion_controller.photos_optional")}</label>
              <input type="file" name="photos" multiple accept="image/*" capture="environment" class="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-red-600 file:text-white hover:file:bg-red-700">
              <p class="text-gray-500 text-xs mt-1">${t("javascript.controllers.checklist_completion_controller.multiple_photos_hint")}</p>
            </div>
          </div>

          <div class="flex gap-3 mt-6">
            <button type="button" onclick="this.closest('.fixed').remove()" class="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors">
              ${t("javascript.controllers.checklist_completion_controller.cancel")}
            </button>
            <button type="submit" class="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors">
              ${t("javascript.controllers.checklist_completion_controller.submit_failure_report")}
            </button>
          </div>
        </form>
      </div>
    `

    document.body.appendChild(modal)

    // Handle form submission
    const form = modal.querySelector('#failure-form')
    form.addEventListener('submit', async (e) => {
      e.preventDefault()

      const formData = new FormData(form)
      
      // We need to build a new FormData that matches what the API expects
      const submissionData = new FormData()
      submissionData.append('checklist_item_id', itemId)
      submissionData.append('value', 'false')
      submissionData.append('is_failure', 'true')
      submissionData.append('failure_notes', formData.get('description'))
      
      submissionData.append('failure_report[severity]', formData.get('severity'))
      submissionData.append('failure_report[description]', formData.get('description'))
      submissionData.append('failure_report[corrective_action]', formData.get('corrective_action'))
      
      // Append all photos
      const photoInput = form.querySelector('input[name="photos"]')
      if (photoInput && photoInput.files.length > 0) {
        for (let i = 0; i < photoInput.files.length; i++) {
          submissionData.append('failure_report[photos][]', photoInput.files[i])
        }
      }

      try {
        const response = await fetch(`/api/document_signatures/${this.signatureIdValue}/checklist_responses`, {
          method: 'POST',
          headers: {
            'X-CSRF-Token': this.csrfToken,
            'Accept': 'application/json'
          },
          body: submissionData
        })

        const data = await response.json()

        if (response.ok && data.success) {
          this.updateProgress(data.data.progress)
          this.markItemComplete(itemId)
          modal.remove()

          // Show success message
          alert(t('javascript.controllers.checklist_completion_controller.failure_reported_successfully_management_has_been_'))
        } else {
          alert(t('javascript.controllers.checklist_completion_controller.failed_to_submit_failure_report', { error: data.error || t('javascript.controllers.checklist_completion_controller.unknown_error') }))
        }
      } catch (error) {
        console.error('Error submitting failure:', error)
        alert(t('javascript.controllers.checklist_completion_controller.an_error_occurred_please_try_again'))
      }
    })
  }

  #escAttr(str) {
    return String(str).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;")
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }
}
